import Permission from "permissions/permission";
import { LegalEntityAccess } from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { LegalEntityAccessType } from "permissions/resources/legal-entities/types";
import {
  MarketplaceAccess,
  MarketplaceSearchAccess,
} from "permissions/resources/marketplace/marketplace-permissions-resources";
import { CommunicationsAccess } from "permissions/resources/messaging/messaging-permissions-resources";
import { AccessLevel, Scope } from "permissions/resources/types";
import { UserNetworkingAccess } from "permissions/resources/users/users-permissions-resources";
import {
  ContractsAccess,
  FeeSchedulesAccess,
  ProposalsAccess,
  TeamsAccess,
  WorkAgreementsAccess,
} from "permissions/resources/work/work-permissions-resources";

export class SessionPermissions {
  constructor(private readonly _permissions: Permission[]) {}

  public get permissions(): Permission[] {
    return [...this._permissions];
  }

  public get includesAccessToCommunications(): boolean {
    const permission = this._permissions.find((p) => p.resource instanceof CommunicationsAccess);
    if (!permission) return false;
    const resource = permission.resource as CommunicationsAccess;
    return (
      resource.accessLevel !== undefined &&
      resource.accessLevel !== null &&
      resource.accessLevel >= AccessLevel["Read-Only"]
    );
  }

  public get includesSigningAuthority(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof LegalEntityAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as LegalEntityAccess;
      return resource.accessType === LegalEntityAccessType["Signing Authority"];
    });
  }

  public get includesAccessToAdministration(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof LegalEntityAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as LegalEntityAccess;
      return (
        (resource.accessType === LegalEntityAccessType["Member Information"] &&
          resource.scope >= Scope["Entity-Wide"] &&
          resource.accessLevel >= AccessLevel["Read/Write"]) ||
        (resource.accessType === LegalEntityAccessType["Member Permissions"] &&
          resource.scope >= Scope["Entity-Wide"]) ||
        (resource.accessType === LegalEntityAccessType["Member Invitations"] && resource.scope >= Scope["Entity-Wide"])
      );
    });
  }

  public get includesAccessToWork(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof WorkAgreementsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as WorkAgreementsAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesAccessToProposals(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof ProposalsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as ProposalsAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesAccessToTeams(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof TeamsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as TeamsAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesAccessToFeeSchedules(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof FeeSchedulesAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as FeeSchedulesAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesAccessToContracts(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof ContractsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as ContractsAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesAccessToMarketplace(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof MarketplaceAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as MarketplaceAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesAccessToMarketplaceSearch(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof MarketplaceSearchAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as MarketplaceSearchAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesAccessToNetwork(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof UserNetworkingAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as UserNetworkingAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }
}
